import { api } from './client';
import type {
  EmployeePersonalSection,
  EmployeePersonalSectionDecision,
} from '@shared/types';

export type EmployeePersonalPayload = Record<string, unknown>;

export interface EmployeePersonalSectionStateEntry {
  decision: EmployeePersonalSectionDecision | null;
  reviewedAt: string | null;
  reviewedById: string | null;
  sectionHash: string;
  reviewWorkflowKey: string | null;
  adminProcessed: boolean;
  adminProcessedAt: string | null;
  adminProcessedById: string | null;
  adminProcessedSectionHash: string | null;
  adminComment: string | null;
}

export interface EmployeePersonalSnapshot {
  id: string;
  employeeId: string;
  employeeName: string;
  period: string;
  payload: EmployeePersonalPayload;
  payloadHash: string;
  sectionState: Record<EmployeePersonalSection, EmployeePersonalSectionStateEntry>;
  assistMetadata: Record<string, unknown>;
  submittedAt: string | null;
  submittedById: string | null;
  policyVersion: number;
  sourceApp: string;
  diffSections: EmployeePersonalSection[];
  createdAt: string;
  updatedAt: string;
}

export interface EmployeePersonalGate {
  allowed: boolean;
  workflowKey: string;
  period: string;
  policyVersion: number;
  blockingMode: 'BLOCK' | 'WARN' | 'ALLOW';
  reason: 'SNAPSHOT_REQUIRED' | 'REVIEW_REQUIRED' | 'ATTENTION_PENDING' | 'OK';
  missingSections: EmployeePersonalSection[];
  requiredSections: EmployeePersonalSection[];
  attentionSections: EmployeePersonalSection[];
  reviewUrl: string;
}

export interface EmployeePersonalAttention {
  snapshotId: string;
  employeeId: string;
  employeeName: string;
  period: string;
  section: EmployeePersonalSection;
  source: 'SECTION_NEEDS_ATTENTION' | 'SECTION_DIFF';
  decision: EmployeePersonalSectionDecision | null;
  sectionHash: string;
  diff: Record<string, [unknown, unknown]> | null;
  createdAt: string;
}

export interface EmployeePersonalAssistWarning {
  field: string;
  message: string;
  suggested: string | null;
  confidence: 'low' | 'medium' | 'high';
}

export interface EmployeePersonalNameFormsSuggestion {
  lastName: string;
  firstName: string;
  middleName: string;
  forms: Record<string, {
    lastName: string;
    firstName: string;
    middleName: string;
  }>;
}

export interface EmployeePersonalPositionFormsSuggestion {
  position: string;
  forms: Record<string, string>;
}

export interface EmployeePersonalFormsSuggestion {
  name: EmployeePersonalNameFormsSuggestion | null;
  position: EmployeePersonalPositionFormsSuggestion | null;
}

export interface EmployeePersonalAssistResult<T> {
  available: boolean;
  hasWarnings: boolean;
  warnings: EmployeePersonalAssistWarning[];
  suggested: T | null;
  reason: string;
  provenance: {
    source: 'AI_SUGGESTED' | 'AI_UNAVAILABLE' | 'AI_FAILED';
    model: string | null;
    generatedAt: string;
  };
}

export interface EmployeePersonalSnapshotAuditLog {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  modelName: string;
  recordId: string;
  userId: string | null;
  userName: string | null;
  dataBefore: Record<string, unknown> | null;
  dataAfter: Record<string, unknown> | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export const employeePersonalDataApi = {
  getSelfSnapshot: async (period: string) => {
    const { data } = await api.get('/employee-personal-data/me/snapshot', { params: { period } });
    return data as {
      employeeId: string;
      snapshot: EmployeePersonalSnapshot;
      attentionSections: EmployeePersonalSection[];
    };
  },

  getGate: async (period: string, workflowKey = 'default') => {
    const { data } = await api.get('/employee-personal-data/me/gate', {
      params: { period, workflowKey },
    });
    return data as EmployeePersonalGate;
  },

  saveSelfSnapshot: async (body: {
    period: string;
    payload: EmployeePersonalPayload;
    assistMetadata?: Record<string, unknown>;
  }) => {
    const { data } = await api.put('/employee-personal-data/me/snapshot', {
      ...body,
      sourceApp: 'mobile',
    });
    return data as { item: EmployeePersonalSnapshot };
  },

  submitSelfReview: async (body: {
    period: string;
    snapshotId?: string;
    sectionDecisions: Partial<Record<EmployeePersonalSection, EmployeePersonalSectionDecision>>;
    workflowKey?: string;
  }) => {
    const { data } = await api.post('/employee-personal-data/me/snapshot/submit', {
      ...body,
      sourceApp: 'mobile',
    });
    return data as {
      item: {
        id: string;
        allowedAfterSave: boolean;
        submittedAt: string | null;
        attentionSections: EmployeePersonalSection[];
      };
    };
  },

  listAttention: async (params: { period?: string } = {}) => {
    const { data } = await api.get('/employee-personal-data/admin/attention', { params });
    return data as { items: EmployeePersonalAttention[]; meta: { total: number } };
  },

  getSnapshot: async (id: string) => {
    const { data } = await api.get(`/employee-personal-data/admin/snapshots/${id}`);
    return data as EmployeePersonalSnapshot;
  },

  getSnapshotHistory: async (id: string) => {
    const { data } = await api.get(`/employee-personal-data/admin/snapshots/${id}/history`);
    return data as { items: EmployeePersonalSnapshotAuditLog[]; total: number };
  },

  updateSnapshot: async (
    id: string,
    payload: EmployeePersonalPayload,
    assistMetadata?: Record<string, unknown>,
  ) => {
    const { data } = await api.put(`/employee-personal-data/admin/snapshots/${id}`, { payload, assistMetadata });
    return data as { item: EmployeePersonalSnapshot };
  },

  processSection: async (snapshotId: string, section: EmployeePersonalSection, comment?: string) => {
    const { data } = await api.post(
      `/employee-personal-data/admin/snapshots/${snapshotId}/sections/${section}/process`,
      { comment },
    );
    return data as { item: { snapshotId: string; section: EmployeePersonalSection; adminProcessed: boolean } };
  },

  suggestNameForms: async (body: { lastName?: string; firstName?: string; middleName?: string }) => {
    const { data } = await api.post('/employee-personal-data/assist/name-forms', body);
    return data as EmployeePersonalAssistResult<EmployeePersonalNameFormsSuggestion>;
  },

  suggestForms: async (body: { lastName?: string; firstName?: string; middleName?: string; position?: string }) => {
    const { data } = await api.post('/employee-personal-data/assist/forms', body);
    return data as EmployeePersonalAssistResult<EmployeePersonalFormsSuggestion>;
  },

  suggestPositionForms: async (body: { position?: string }) => {
    const { data } = await api.post('/employee-personal-data/assist/position-forms', body);
    return data as EmployeePersonalAssistResult<EmployeePersonalPositionFormsSuggestion>;
  },

  suggestFieldChecks: async (body: { fields: { fieldName: string; fieldLabel: string; value: string }[] }) => {
    const { data } = await api.post('/employee-personal-data/assist/check-fields', body);
    return data as EmployeePersonalAssistResult<Record<string, { hasError: boolean; suggested: string | null; reason: string | null } | null>>;
  },
};
